import React from 'react';
import { Box, Tabs, Tab, Divider, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import {
    Notifications as NotificationsIcon,
    Email as EmailIcon,
    MoveToInbox as MoveToInboxIcon,
    Settings as SettingsIcon,
    Storage as StorageIcon,
    Payment as PaymentIcon,
    SmartToy as SmartToyIcon,
    PictureAsPdf as PictureAsPdfIcon,
    Block as BlockIcon,
    SettingsBackupRestore as SettingsBackupRestoreIcon,
    TrendingUp as TrendingUpIcon,
    Chat as ChatIcon
} from '@mui/icons-material';

const sections = [
    {
        label: 'Messaging',
        tabs: [
            { label: 'Notifications', path: '/admin/config/notifications', icon: <NotificationsIcon /> },
            { label: 'Templates', path: '/admin/config/notification-templates', icon: <EmailIcon /> },
            { label: 'Inbound Mail', path: '/admin/config/inbound-mail', icon: <MoveToInboxIcon /> },
            { label: 'Escalation', path: '/admin/config/escalation', icon: <TrendingUpIcon /> },
            { label: 'WhatsApp', path: '/admin/config/whatsapp', icon: <ChatIcon /> },
        ],
    },
    {
        label: 'Infrastructure',
        tabs: [
            { label: 'System', path: '/admin/config/system', icon: <SettingsIcon /> },
            { label: 'Storage', path: '/admin/config/storage', icon: <StorageIcon /> },
            { label: 'Backup', path: '/admin/config/backup', icon: <SettingsBackupRestoreIcon /> },
        ],
    },
    {
        label: 'Finance',
        tabs: [
            { label: 'Billing', path: '/admin/config/billing', icon: <PaymentIcon /> },
            { label: 'Invoice PDF', path: '/admin/config/invoice-pdf-template', icon: <PictureAsPdfIcon /> },
        ],
    },
    {
        label: 'Tooling',
        tabs: [
            { label: 'AI', path: '/admin/config/ai', icon: <SmartToyIcon /> },
            { label: 'Content Filter', path: '/admin/config/content-filter', icon: <BlockIcon /> },
        ],
    },
];

// Flatten for index-based tab selection
const allTabs = sections.flatMap((s) => s.tabs);

const SectionLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightSemiBold,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: theme.palette.text.disabled,
    padding: theme.spacing(0, 1),
    alignSelf: 'center',
    whiteSpace: 'nowrap',
}));

const SectionDivider = styled(Divider)(({ theme }) => ({
    alignSelf: 'center',
    height: 24,
    margin: theme.spacing(0, 0.5),
}));

const SubMenuRoot = styled(Box)(({ theme }) => ({
    borderBottom: 1,
    borderColor: theme.palette.divider,
    marginBottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'flex-end',
    overflow: 'auto',
}));

const SectionColumn = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
});

const StyledTabs = styled(Tabs)({
    minHeight: 48,
});

const StyledTab = styled(Tab)({
    minHeight: 48,
    textTransform: 'none',
});

const ConfigSubMenu = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const currentTab = allTabs.findIndex((tab) => location.pathname === tab.path);

    const handleChange = (event, newValue) => {
        navigate(allTabs[newValue].path);
    };

    return (
        <SubMenuRoot>
            {sections.map((section, sectionIdx) => (
                <React.Fragment key={section.label}>
                    {sectionIdx > 0 && <SectionDivider orientation="vertical" flexItem />}
                    <SectionColumn>
                        <SectionLabel>{section.label}</SectionLabel>
                        <StyledTabs
                            value={
                                section.tabs.some((t) => t.path === location.pathname)
                                    ? section.tabs.findIndex((t) => t.path === location.pathname)
                                    : false
                            }
                            onChange={(_, localIdx) => {
                                const globalIdx = allTabs.findIndex((t) => t.path === section.tabs[localIdx].path);
                                handleChange(null, globalIdx);
                            }}
                        >
                            {section.tabs.map((tab) => (
                                <StyledTab
                                    key={tab.path}
                                    label={tab.label}
                                    icon={tab.icon}
                                    iconPosition="start"
                                />
                            ))}
                        </StyledTabs>
                    </SectionColumn>
                </React.Fragment>
            ))}
        </SubMenuRoot>
    );
};

export default ConfigSubMenu;
